import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, Loader2, Sparkles } from 'lucide-react';
import { useMyTier } from '../hooks/useMyTier';

const FEATURE_BLURB = {
  manage_listings: {
    title: 'Manage Listings is a Business Growth feature',
    body: 'Upgrade to add and edit multiple listings, categories, and marketing details.',
  },
  coupons: {
    title: 'Coupons are a Business Growth feature',
    body: 'Design and publish unlimited coupons that redeem straight from the app.',
  },
  loyalty: {
    title: 'Loyalty Programs are a Business Growth feature',
    body: 'Reward repeat visits with digital stamp cards, QR check-ins, and auto-redemption.',
  },
  reviews_verification: {
    title: 'Review Verification is a Business Growth feature',
    body: 'Issue location-locked review invites and prevent fake reviews.',
  },
  tracking_board: {
    title: 'Customer Tracking Board is a Business Growth feature',
    body: 'See every user who engaged with your business — bids, redemptions, visits.',
  },
  landing_page_builder: {
    title: 'Landing Page Builder is a Full Automation feature',
    body: 'Auto-generate a branded one-page site linked to your listing.',
  },
  form_builder: {
    title: 'Form Builder is a Full Automation feature',
    body: 'Build sign-up forms + surveys and drop leads straight into your customer list.',
  },
  marketing_campaigns: {
    title: 'Marketing Campaigns are a Full Automation feature',
    body: 'Automate email + SMS blasts to your customer list.',
  },
  automation: {
    title: 'Automation is a Full Automation feature',
    body: 'Trigger workflows the moment a customer bids, checks in, or redeems.',
  },
};

/**
 * <TierGate feature="coupons">…children…</TierGate>
 * If the current owner's tier includes the feature, renders children.
 * Otherwise renders an inline upgrade card pointing at /business/upgrade.
 */
export default function TierGate({ feature, children, fallback }) {
  const { tier, loading, hasFeature } = useMyTier();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 py-10">
        <Loader2 className="animate-spin" size={16}/> Checking your plan…
      </div>
    );
  }
  if (hasFeature(feature)) return children;
  if (fallback) return fallback;

  const blurb = FEATURE_BLURB[feature] || {
    title: 'Upgrade required',
    body: 'This feature is available on a higher business plan.',
  };
  const isFullAutomationOnly = ['landing_page_builder','form_builder','marketing_campaigns','automation'].includes(feature);

  return (
    <div className="pbb-card p-8 md:p-12 border-2 border-dashed border-yellow-500/40 bg-yellow-500/5 text-center" data-testid={`tier-gate-${feature}`}>
      <div className="mx-auto w-14 h-14 rounded-full bg-yellow-500/15 flex items-center justify-center">
        <Lock className="text-yellow-300" size={26}/>
      </div>
      <div className="mt-4 text-[11px] uppercase tracking-widest text-yellow-300 font-semibold">Your plan: {tier?.label || 'Category Listing'}</div>
      <h2 className="mt-2 text-xl md:text-2xl font-bold">{blurb.title}</h2>
      <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">{blurb.body}</p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/business/upgrade" data-testid={`tier-gate-upgrade-${feature}`}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm">
          <Crown size={14}/> {isFullAutomationOnly ? 'Upgrade to Full Automation' : 'Upgrade to Business Growth'}
        </Link>
        <Link to="/business-dashboard"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#2a2a2a] hover:border-yellow-500/50 text-gray-300 text-sm">
          Back to dashboard
        </Link>
      </div>
      <div className="mt-6 text-[11px] text-gray-500 flex items-center justify-center gap-1">
        <Sparkles size={12}/> Every upgrade includes an instant unlock — no waiting for approvals.
      </div>
    </div>
  );
}
